import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  Divide,
  Calculator,
  ChevronRight,
  Home,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  Plus,
  Minus,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Helper functions
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
};

const simplifyFraction = (numerator, denominator) => {
  if (denominator === 0) return { num: 0, den: 0, isValid: false };
  const divisor = gcd(numerator, denominator);
  let num = numerator / divisor;
  let den = denominator / divisor;
  
  // Handle negative fractions
  if (den < 0) {
    num = -num;
    den = -den;
  }
  
  return { num, den, isValid: true, gcd: divisor };
};

const addFractions = (n1, d1, n2, d2) => {
  const num = n1 * d2 + n2 * d1;
  const den = d1 * d2;
  return simplifyFraction(num, den);
};

const subtractFractions = (n1, d1, n2, d2) => {
  const num = n1 * d2 - n2 * d1;
  const den = d1 * d2;
  return simplifyFraction(num, den);
};

const multiplyFractions = (n1, d1, n2, d2) => {
  const num = n1 * n2;
  const den = d1 * d2;
  return simplifyFraction(num, den);
};

const divideFractions = (n1, d1, n2, d2) => {
  if (n2 === 0) return { num: 0, den: 0, isValid: false };
  const num = n1 * d2;
  const den = d1 * n2;
  return simplifyFraction(num, den);
};

// Fraction Visualization Component
const FractionVisual = ({ numerator, denominator, label }) => {
  const simplified = simplifyFraction(numerator, denominator);
  if (!simplified.isValid) return null;
  
  const { num, den } = simplified;
  const percentage = Math.abs((num / den) * 100);
  const filled = Math.min(Math.ceil(percentage / 10), 10);
  
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground text-center">{label}</div>
      <div className="flex justify-center items-center gap-2">
        <span className="text-2xl font-bold font-mono text-foreground">{num}</span>
        <div className="w-8 h-0.5 bg-foreground"></div>
        <span className="text-2xl font-bold font-mono text-foreground">{den}</span>
      </div>
      <div className="grid grid-cols-10 gap-1 mt-2">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className={`h-6 rounded ${
              i < filled ? 'bg-primary' : 'bg-muted'
            } transition-all duration-300`}
          />
        ))}
      </div>
      <div className="text-xs text-center text-muted-foreground">
        {percentage.toFixed(1)}%
      </div>
    </div>
  );
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Main Calculator Component
export default function FractionCalculator() {
  const [num1, setNum1] = useState(1);
  const [den1, setDen1] = useState(2);
  const [num2, setNum2] = useState(1);
  const [den2, setDen2] = useState(3);
  const [operation, setOperation] = useState('add');
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  // Calculate results
  const results = useMemo(() => {
    const ops = {
      add: addFractions(num1, den1, num2, den2),
      subtract: subtractFractions(num1, den1, num2, den2),
      multiply: multiplyFractions(num1, den1, num2, den2),
      divide: divideFractions(num1, den1, num2, den2),
    };
    
    const simplified1 = simplifyFraction(num1, den1);
    const simplified2 = simplifyFraction(num2, den2);
    
    return {
      operations: ops,
      fraction1: simplified1,
      fraction2: simplified2,
      decimal1: simplified1.isValid ? (simplified1.num / simplified1.den).toFixed(6) : 'Invalid',
      decimal2: simplified2.isValid ? (simplified2.num / simplified2.den).toFixed(6) : 'Invalid',
    };
  }, [num1, den1, num2, den2]);

  const currentResult = results.operations[operation];

  const operationSymbols = {
    add: '+',
    subtract: '−',
    multiply: '×',
    divide: '÷',
  };

  const operationIcons = {
    add: Plus,
    subtract: Minus,
    multiply: X,
    divide: Divide,
  };

  return (
    <>
      <Helmet>
        <title>Fraction Calculator - Simplify, Add, Subtract, Multiply & Divide | MathCalcPro</title>
        <meta name="description" content="Free online fraction calculator. Simplify fractions and perform operations: add, subtract, multiply, and divide with step-by-step solutions and visual representations." />
        <meta name="keywords" content="fraction calculator, simplify fractions, fraction operations, add fractions, multiply fractions, divide fractions" />
        <link rel="canonical" href="https://mathcalcpro.com/calculators/fractions" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
            <Home className="w-3 h-3" />
            <span>Home</span>
          </Link>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <span className="text-foreground font-medium">Fraction Calculator</span>
        </nav>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Divide className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Fraction Calculator</h1>
              <p className="text-muted-foreground">Simplify and perform operations on fractions</p>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Calculator Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <Card className="glass-strong rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>

              {/* Fraction Inputs */}
              <div className="space-y-6">
                {/* First Fraction */}
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">First Fraction</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={num1}
                      onChange={(e) => setNum1(parseInt(e.target.value) || 0)}
                      className="neumorph-inset border-0 text-center font-mono text-lg"
                      placeholder="1"
                    />
                    <div className="w-6 h-0.5 bg-foreground flex-shrink-0"></div>
                    <Input
                      type="number"
                      value={den1}
                      onChange={(e) => setDen1(parseInt(e.target.value) || 1)}
                      className="neumorph-inset border-0 text-center font-mono text-lg"
                      placeholder="2"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 text-center">
                    Decimal: {results.decimal1}
                  </div>
                </div>

                {/* Operation Selector */}
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Operation</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {['add', 'subtract', 'multiply', 'divide'].map((op) => {
                      const Icon = operationIcons[op];
                      return (
                        <Button
                          key={op}
                          variant={operation === op ? 'default' : 'outline'}
                          className="h-12"
                          onClick={() => setOperation(op)}
                        >
                          <Icon className="w-4 h-4" />
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Second Fraction */}
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Second Fraction</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={num2}
                      onChange={(e) => setNum2(parseInt(e.target.value) || 0)}
                      className="neumorph-inset border-0 text-center font-mono text-lg"
                      placeholder="1"
                    />
                    <div className="w-6 h-0.5 bg-foreground flex-shrink-0"></div>
                    <Input
                      type="number"
                      value={den2}
                      onChange={(e) => setDen2(parseInt(e.target.value) || 1)}
                      className="neumorph-inset border-0 text-center font-mono text-lg"
                      placeholder="3"
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 text-center">
                    Decimal: {results.decimal2}
                  </div>
                </div>

                {/* Quick Examples */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { n1: 1, d1: 2, n2: 1, d2: 4, label: '1/2 + 1/4' },
                      { n1: 3, d1: 4, n2: 1, d2: 2, label: '3/4 − 1/2' },
                      { n1: 2, d1: 3, n2: 3, d2: 5, label: '2/3 × 3/5' },
                    ].map((ex, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          setNum1(ex.n1);
                          setDen1(ex.d1);
                          setNum2(ex.n2);
                          setDen2(ex.d2);
                        }}
                      >
                        {ex.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Result */}
                <div className="pt-4 border-t border-border">
                  <Label className="text-sm font-medium text-foreground mb-3 block">Result</Label>
                  {currentResult.isValid ? (
                    <div className="neumorph-sm rounded-xl p-4">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-3xl font-bold font-mono text-primary">{currentResult.num}</span>
                        <div className="w-10 h-0.5 bg-primary"></div>
                        <span className="text-3xl font-bold font-mono text-primary">{currentResult.den}</span>
                        <CopyButton text={`${currentResult.num}/${currentResult.den}`} />
                      </div>
                      <div className="text-center text-sm text-muted-foreground">
                        Decimal: {(currentResult.num / currentResult.den).toFixed(6)}
                      </div>
                      {currentResult.den === 1 && (
                        <Badge variant="secondary" className="mt-2 w-full justify-center">
                          Whole Number
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-destructive p-4 neumorph-sm rounded-xl">
                      Invalid operation (division by zero)
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Center/Right Column - Visualization & Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visualization Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-foreground flex items-center gap-2">
                    <Info className="w-4 h-4 text-primary" />
                    Visual Representation
                  </h2>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <FractionVisual numerator={num1} denominator={den1} label="First Fraction" />
                  <FractionVisual
                    numerator={currentResult.num}
                    denominator={currentResult.den}
                    label={`Result (${operationSymbols[operation]})`}
                  />
                  <FractionVisual numerator={num2} denominator={den2} label="Second Fraction" />
                </div>
              </Card>
            </motion.div>

            {/* Step-by-Step Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="glass rounded-2xl p-6">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-primary" />
                  Step-by-Step Solution
                </h2>

                <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                  <AccordionItem value="step-1" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">1</span>
                        <span className="font-medium">Identify the fractions</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2">
                        <p className="text-muted-foreground">
                          First fraction: <span className="font-mono text-foreground font-semibold">{num1}/{den1}</span>
                        </p>
                        <p className="text-muted-foreground">
                          Second fraction: <span className="font-mono text-foreground font-semibold">{num2}/{den2}</span>
                        </p>
                        <p className="text-muted-foreground">
                          Operation: <span className="font-mono text-foreground font-semibold">{operation}</span>
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-2" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">2</span>
                        <span className="font-medium">Apply the operation</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-3">
                        {operation === 'add' && (
                          <>
                            <p className="text-muted-foreground">For addition, find common denominator:</p>
                            <div className="formula-display">
                              ({num1} × {den2} + {num2} × {den1}) / ({den1} × {den2}) = {num1 * den2 + num2 * den1}/{den1 * den2}
                            </div>
                          </>
                        )}
                        {operation === 'subtract' && (
                          <>
                            <p className="text-muted-foreground">For subtraction, find common denominator:</p>
                            <div className="formula-display">
                              ({num1} × {den2} − {num2} × {den1}) / ({den1} × {den2}) = {num1 * den2 - num2 * den1}/{den1 * den2}
                            </div>
                          </>
                        )}
                        {operation === 'multiply' && (
                          <>
                            <p className="text-muted-foreground">For multiplication, multiply numerators and denominators:</p>
                            <div className="formula-display">
                              ({num1} × {num2}) / ({den1} × {den2}) = {num1 * num2}/{den1 * den2}
                            </div>
                          </>
                        )}
                        {operation === 'divide' && (
                          <>
                            <p className="text-muted-foreground">For division, multiply by reciprocal:</p>
                            <div className="formula-display">
                              ({num1} × {den2}) / ({den1} × {num2}) = {num1 * den2}/{den1 * num2}
                            </div>
                          </>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-3" className="border-0">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">3</span>
                        <span className="font-medium">Simplify the result</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2">
                        {currentResult.gcd > 1 ? (
                          <>
                            <p className="text-muted-foreground">
                              GCD of numerator and denominator: <span className="font-mono text-foreground">{currentResult.gcd}</span>
                            </p>
                            <p className="text-muted-foreground">
                              Simplified: <span className="font-mono text-primary font-semibold text-lg">{currentResult.num}/{currentResult.den}</span>
                            </p>
                          </>
                        ) : (
                          <p className="text-muted-foreground">
                            Fraction is already in simplest form: <span className="font-mono text-primary font-semibold text-lg">{currentResult.num}/{currentResult.den}</span>
                          </p>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            </motion.div>

            {/* Deep Dive Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              {/* What are Fractions */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">What are Fractions?</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  A fraction represents a part of a whole. It consists of a numerator (top number) and a denominator (bottom number). 
                  The numerator indicates how many parts we have, while the denominator shows how many equal parts make up the whole.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  For example, in the fraction 3/4, we have 3 parts out of 4 equal parts. Fractions are essential in everyday life 
                  for measuring quantities, dividing resources, and understanding proportions.
                </p>
              </Card>

              {/* Fraction Operations */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Fraction Operations Guide
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { name: 'Addition', formula: 'a/b + c/d = (ad + bc)/bd', desc: 'Find common denominator' },
                    { name: 'Subtraction', formula: 'a/b − c/d = (ad − bc)/bd', desc: 'Find common denominator' },
                    { name: 'Multiplication', formula: 'a/b × c/d = (a×c)/(b×d)', desc: 'Multiply across' },
                    { name: 'Division', formula: 'a/b ÷ c/d = (a×d)/(b×c)', desc: 'Multiply by reciprocal' },
                  ].map((item, i) => (
                    <div key={i} className="bg-muted/30 rounded-xl p-4">
                      <span className="text-sm font-semibold text-foreground block mb-1">{item.name}</span>
                      <code className="font-mono text-xs text-muted-foreground block mb-1">{item.formula}</code>
                      <span className="text-xs text-muted-foreground">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Real World Applications */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
                <ul className="space-y-3">
                  {[
                    'Cooking and baking: Adjusting recipe quantities and measurements',
                    'Construction: Measuring materials, calculating dimensions, and scaling blueprints',
                    'Finance: Understanding interest rates, profit margins, and stock prices',
                    'Time management: Dividing tasks and calculating portions of time',
                    'Music: Understanding rhythm, beats, and time signatures',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Common Mistakes */}
              <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  Common Mistakes to Avoid
                </h2>
                <ul className="space-y-3">
                  {[
                    'Adding/subtracting numerators and denominators separately (3/4 + 1/2 ≠ 4/6)',
                    'Forgetting to simplify the final answer to lowest terms',
                    'When dividing, multiplying by the fraction instead of its reciprocal',
                    'Not finding a common denominator before adding or subtracting',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* FAQ Section */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    {
                      q: 'What is a simplified fraction?',
                      a: 'A simplified (or reduced) fraction is when the numerator and denominator have no common factors other than 1. For example, 6/8 simplifies to 3/4 by dividing both by their GCD of 2.'
                    },
                    {
                      q: 'How do I add fractions with different denominators?',
                      a: 'Find a common denominator (usually the least common multiple of both denominators), convert both fractions to equivalent fractions with this denominator, then add the numerators while keeping the denominator the same.'
                    },
                    {
                      q: 'What is an improper fraction?',
                      a: 'An improper fraction is when the numerator is greater than or equal to the denominator (e.g., 7/4). It can be converted to a mixed number (1 3/4) by dividing the numerator by the denominator.'
                    },
                    {
                      q: 'Why do we flip the second fraction when dividing?',
                      a: 'Dividing by a fraction is the same as multiplying by its reciprocal. This is because (a/b) ÷ (c/d) = (a/b) × (d/c). It\'s a mathematical property that makes division simpler to compute.'
                    },
                    {
                      q: 'Can a fraction have a negative value?',
                      a: 'Yes! A negative fraction can have the negative sign in the numerator, denominator, or in front of the fraction. By convention, we usually write it in front: −3/4 rather than 3/(−4).'
                    }
                  ].map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}